(() => {
  'use strict';

  if (window.__AmaraRewardProgramLoaded) return;
  window.__AmaraRewardProgramLoaded = true;

  // Legacy reward attributes/properties are kept only so old free gifts can be
  // removed safely from carts that still contain them.
  const ATTR_TYPE = 'amara_reward_type';
  const ATTR_TITLE = 'amara_reward_title';
  const ATTR_VARIANT = 'amara_reward_variant';
  const PROP_REWARD = '_amara_reward';

  const DRAWER_SECTION = 'cart-drawer';
  const CART_SECTION = 'cart-template';

  let busy = false;
  let reconcileTimer = null;
  let warnedMissingCode = false;

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const rewardRoots = () => [...document.querySelectorAll('[data-amara-reward-program]')];
  const firstRoot = () => document.querySelector('[data-amara-reward-program]');

  const getConfig = () => {
    const root = firstRoot();
    if (!root) return null;

    return {
      threshold: Number(root.dataset.threshold || 55000),
      discountCode: String(root.dataset.discountCode || '').trim()
    };
  };

  const fetchCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' }
    });

    if (!response.ok) {
      throw new Error('Your bag could not be checked. Please try again.');
    }

    return response.json();
  };

  const legacyRewardItems = (cart) => (cart.items || []).filter(
    (item) => String(item.properties?.[PROP_REWARD]) === 'true'
  );

  const qualifyingTotal = (cart) => (cart.items || []).reduce((total, item) => {
    if (String(item.properties?.[PROP_REWARD]) === 'true') return total;
    return total + Number(item.original_line_price ?? item.line_price ?? 0);
  }, 0);

  const customerItemCount = (cart) => (cart.items || []).reduce((total, item) => {
    if (String(item.properties?.[PROP_REWARD]) === 'true') return total;
    return total + Number(item.quantity || 0);
  }, 0);

  const discountApplications = (cart) => (
    Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : []
  );

  const activeDiscountCodes = (cart) => {
    const codes = [];

    discountApplications(cart).forEach((application) => {
      if (application?.type === 'discount_code' && application?.title) {
        codes.push(String(application.title).trim());
      }
    });

    if (Array.isArray(cart?.discount_codes)) {
      cart.discount_codes.forEach((discount) => {
        const value = discount?.code || discount?.title;
        if (value && discount?.applicable !== false) {
          codes.push(String(value).trim());
        }
      });
    }

    return [...new Set(codes.filter(Boolean))];
  };

  const hasDiscountCode = (cart, code) => {
    const wanted = String(code || '').trim().toLowerCase();
    if (!wanted) return false;

    return activeDiscountCodes(cart).some(
      (item) => item.toLowerCase() === wanted
    );
  };

  const codesWithoutOffer = (cart, offerCode) => {
    const wanted = String(offerCode || '').trim().toLowerCase();

    return activeDiscountCodes(cart).filter(
      (code) => code.toLowerCase() !== wanted
    );
  };

  const buildLegacyRewardRemovalUpdates = (cart) => {
    const updates = {};

    legacyRewardItems(cart).forEach((item) => {
      updates[item.key] = 0;
    });

    return updates;
  };

  const hasLegacyAttributes = (cart) => (
    Boolean(cart.attributes?.[ATTR_TYPE])
    || Boolean(cart.attributes?.[ATTR_TITLE])
    || Boolean(cart.attributes?.[ATTR_VARIANT])
  );

  const clearedLegacyAttributes = () => ({
    [ATTR_TYPE]: '',
    [ATTR_TITLE]: '',
    [ATTR_VARIANT]: ''
  });

  const discountAttributes = () => ({
    [ATTR_TYPE]: 'discount',
    [ATTR_TITLE]: '10% off',
    [ATTR_VARIANT]: ''
  });

  const requestCart = async (endpoint, payload, includeSection = true) => {
    const body = { ...payload };

    if (includeSection) {
      body.sections = [DRAWER_SECTION, CART_SECTION];
      body.sections_url = window.location.pathname || '/';
    }

    const response = await fetch(`${rootUrl()}${endpoint}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    });

    const result = await response.json().catch(() => ({}));

    if (!response.ok) {
      throw new Error(
        result.description
        || result.message
        || 'The Amara offer could not be updated.'
      );
    }

    return result;
  };

  const updateCart = (payload, includeSection = true) => (
    requestCart('cart/update.js', payload, includeSection)
  );

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  const replaceInner = (selector, nextDocument) => {
    const current = document.querySelector(selector);
    const next = nextDocument.querySelector(selector);

    if (current && next) {
      current.innerHTML = next.innerHTML;
    }
  };

  const updateCartCount = (cart) => {
    const count = customerItemCount(cart);

    document.querySelectorAll('.m-cart-count-bubble').forEach((bubble) => {
      bubble.textContent = count;
      bubble.classList.toggle('m:hidden', count === 0);
      bubble.classList.toggle('hidden', count === 0);
    });
  };

  const renderCartUI = (cart) => {
    const drawerHtml = cart?.sections?.[DRAWER_SECTION];
    const cartHtml = cart?.sections?.[CART_SECTION];

    if (document.querySelector('m-cart-drawer') && drawerHtml) {
      const nextDocument = parseHTML(drawerHtml);
      replaceInner('[data-amara-cart-top]', nextDocument);
      replaceInner('[data-minimog-cart-items]', nextDocument);
      replaceInner('[data-amara-cart-footer]', nextDocument);
    }

    if (document.querySelector('#MinimogCart') && cartHtml) {
      const nextDocument = parseHTML(cartHtml);
      replaceInner('[data-minimog-cart-items]', nextDocument);
      replaceInner('[data-amara-cart-page-reward]', nextDocument);
      replaceInner('[data-minimog-cart-discounts]', nextDocument);
      replaceInner('[data-cart-subtotal]', nextDocument);
      replaceInner('[data-minimog-gift-wrapping]', nextDocument);
    }

    document.querySelector('m-cart-drawer')?.classList.toggle(
      'm-cart--empty',
      Number(cart?.item_count || 0) === 0
    );

    document.querySelector('#MinimogCart')?.classList.toggle(
      'm-cart--empty',
      Number(cart?.item_count || 0) === 0
    );

    updateCartCount(cart);
    window.FoxKitV2?.Modules?.InCart?.getCart?.();
  };

  const showMessage = (message, type = 'success') => {
    rewardRoots().forEach((root) => {
      const box = root.querySelector('[data-amara-reward-message]');
      if (!box) return;

      box.hidden = false;
      box.textContent = message;
      box.dataset.type = type;

      clearTimeout(box._amaraRewardTimer);
      box._amaraRewardTimer = setTimeout(() => {
        box.hidden = true;
      }, 4300);
    });

    if (type === 'error' && window.MinimogTheme?.Notification?.show) {
      window.MinimogTheme.Notification.show({
        type: 'warning',
        message
      });
    }
  };

  const track = (eventName, data = {}) => {
    const detail = { event: eventName, ...data };
    window.dataLayer?.push(detail);
    document.dispatchEvent(new CustomEvent(`amara:${eventName}`, { detail }));
  };

  const reconcile = async () => {
    if (busy || !firstRoot()) return;

    const config = getConfig();
    if (!config) return;

    busy = true;

    try {
      const cart = await fetchCart();
      const total = qualifyingTotal(cart);
      const legacyItems = legacyRewardItems(cart);
      const offerApplied = hasDiscountCode(cart, config.discountCode);
      const otherCodes = codesWithoutOffer(cart, config.discountCode);
      const legacyAttributesPresent = hasLegacyAttributes(cart);

      // Always clean up any old free-glasses/perfume reward items left in a cart.
      const cleanupNeeded = legacyItems.length > 0;

      if (total < config.threshold) {
        if (cleanupNeeded || legacyAttributesPresent || offerApplied) {
          const result = await updateCart({
            updates: buildLegacyRewardRemovalUpdates(cart),
            discount: otherCodes.join(','),
            attributes: clearedLegacyAttributes()
          });

          renderCartUI(result);

          if (offerApplied) {
            showMessage('10% off was removed because the bag is below AED 550.', 'error');
            track('offer_removed', { reason: 'below_threshold' });
          }
        }

        return;
      }

      if (!config.discountCode) {
        if (!warnedMissingCode) {
          warnedMissingCode = true;
          showMessage('Add the 10% discount code in Theme settings.', 'error');
          console.warn('[Amara Offer] No discount code is configured.');
        }

        if (cleanupNeeded || legacyAttributesPresent) {
          const result = await updateCart({
            updates: buildLegacyRewardRemovalUpdates(cart),
            attributes: clearedLegacyAttributes()
          });

          renderCartUI(result);
        }

        return;
      }

      warnedMissingCode = false;

      // Respect another manually-entered discount code rather than silently
      // deleting it. The Amara code will auto-apply once that code is removed.
      if (otherCodes.length > 0 && !offerApplied) {
        if (cleanupNeeded || legacyAttributesPresent) {
          const result = await updateCart({
            updates: buildLegacyRewardRemovalUpdates(cart),
            attributes: clearedLegacyAttributes()
          });

          renderCartUI(result);
        }

        return;
      }

      if (!offerApplied) {
        const result = await updateCart({
          updates: buildLegacyRewardRemovalUpdates(cart),
          discount: config.discountCode,
          attributes: discountAttributes()
        });

        if (!hasDiscountCode(result, config.discountCode)) {
          const cleaned = await updateCart({
            updates: buildLegacyRewardRemovalUpdates(result),
            discount: codesWithoutOffer(result, config.discountCode).join(','),
            attributes: clearedLegacyAttributes()
          });

          renderCartUI(cleaned);
          showMessage(
            `The code ${config.discountCode} is not active or is not valid for this bag.`,
            'error'
          );
          return;
        }

        renderCartUI(result);
        showMessage('10% off applied automatically.');
        track('offer_applied', { discount_code: config.discountCode });
        return;
      }

      // The offer is already applied. Only clean old reward data if needed.
      const typeIsDiscount = String(cart.attributes?.[ATTR_TYPE] || '') === 'discount';

      if (cleanupNeeded || !typeIsDiscount) {
        const result = await updateCart({
          updates: buildLegacyRewardRemovalUpdates(cart),
          attributes: discountAttributes()
        });

        renderCartUI(result);
      }
    } catch (error) {
      console.warn('[Amara Offer]', error);
    } finally {
      busy = false;
    }
  };

  const scheduleReconcile = (delay = 160) => {
    clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(reconcile, delay);
  };

  document.addEventListener('shopify:section:load', () => scheduleReconcile(180));
  document.addEventListener('amara:cart-added', () => scheduleReconcile(180));

  const subscribeToThemeCart = () => {
    if (
      window.MinimogEvents?.subscribe
      && window.MinimogTheme?.pubSubEvents?.cartUpdate
    ) {
      window.MinimogEvents.subscribe(
        window.MinimogTheme.pubSubEvents.cartUpdate,
        () => scheduleReconcile(180)
      );
      return true;
    }

    return false;
  };

  if (!subscribeToThemeCart()) {
    window.addEventListener('load', subscribeToThemeCart, { once: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => scheduleReconcile(260),
      { once: true }
    );
  } else {
    scheduleReconcile(260);
  }
})();